import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import { adminAPI } from "@/lib/api";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  Shield, 
  Lock, 
  User, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  LogOut,
  FileText,
  Settings
} from "lucide-react";

const PLATFORM_LABELS = {
  htb: "Hack The Box",
  proving_grounds: "Proving Grounds",
  other: "Other",
};

const AdminPage = () => {
  const { isAuthenticated, login, logout, checkSession, getAuthHeader } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [writeups, setWriteups] = useState([]);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    const init = async () => {
      await checkSession();
      setLoading(false);
    };
    init();
  }, [checkSession]);

  const fetchWriteups = useCallback(async () => {
    try {
      const response = await adminAPI.getAllWriteups(getAuthHeader());
      setWriteups(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
      }
      toast.error("Failed to fetch writeups");
    }
  }, [getAuthHeader, logout]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWriteups();
    }
  }, [isAuthenticated, fetchWriteups]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    
    const success = await login(loginForm.username, loginForm.password);
    if (success) {
      toast.success("Logged in successfully");
      setLoginForm({ username: "", password: "" });
    }
    
    setLoggingIn(false);
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    
    try {
      await adminAPI.deleteWriteup(id, getAuthHeader());
      toast.success("Writeup deleted");
      fetchWriteups();
    } catch (error) {
      toast.error("Failed to delete writeup");
    }
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out");
  };

  const publishedCount = writeups.filter((w) => w.published).length;
  const draftCount = writeups.filter((w) => !w.published).length;
  const totalViews = writeups.reduce((sum, w) => sum + (w.views || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-text-muted font-mono">Loading...</div>
      </div>
    );
  }

  // Login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[radial-gradient(circle_at_top,rgba(0,255,136,0.08),transparent_35%)] py-12" data-testid="admin-login-page">
        <SEO
          title="Admin Access"
          description="Restricted administration area for ZeroDay.log."
          canonical="/admin"
          robots="noindex,nofollow"
        />
        <div className="mx-auto grid w-full max-w-5xl gap-10 px-6 lg:grid-cols-[1fr_420px] lg:items-center">
          <div className="hidden lg:block">
            <div className="mb-4 inline-flex items-center gap-2 rounded-sm border border-border bg-background-surface/80 px-4 py-2 font-mono text-xs text-text-muted">
              <span className="text-accent-primary">$</span>
              admin --restricted --audit
            </div>
            <h1 className="font-heading text-5xl font-bold leading-[0.95] text-text-primary">
              Restricted control surface for publishing and managing writeups.
            </h1>
            <p className="mt-5 max-w-xl text-sm leading-8 text-text-secondary">
              This area is intentionally isolated for secure content operations. Authentication is required before accessing writeup management, draft state, and editorial controls.
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              <div className="border border-border bg-background-surface p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-text-muted">Access</div>
                <div className="mt-2 font-heading text-xl text-text-primary">Protected</div>
              </div>
              <div className="border border-border bg-background-surface p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-text-muted">Scope</div>
                <div className="mt-2 font-heading text-xl text-text-primary">Editorial</div>
              </div>
              <div className="border border-border bg-background-surface p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-text-muted">Mode</div>
                <div className="mt-2 font-heading text-xl text-text-primary">Operator</div>
              </div>
            </div>
          </div>

          <div className="w-full max-w-md justify-self-center px-0 lg:max-w-none">
          {/* Header */}
          <div className="border border-border bg-background-surface p-8 shadow-[0_24px_70px_rgba(0,0,0,0.22)]">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="flex h-16 w-16 items-center justify-center border border-accent-primary bg-accent-primary/5">
                <Shield className="w-8 h-8 text-accent-primary" />
              </div>
            </div>
            <h1 className="font-heading font-bold text-2xl text-text-primary mb-2">
              Admin Access
            </h1>
            <p className="text-text-muted text-sm font-mono">
              // Restricted area
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-text-secondary text-sm font-mono">
                Username
              </Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <Input
                  id="username"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  placeholder="Enter username"
                  className="input-field pl-12"
                  required
                  autoComplete="username"
                  data-testid="admin-username-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-text-secondary text-sm font-mono">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <Input
                  id="password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  placeholder="••••••••"
                  className="input-field pl-12"
                  required
                  autoComplete="current-password"
                  data-testid="admin-password-input"
                />
              </div>
            </div>

            {/* Honeypot field - hidden from users, bots will fill it */}
            <input 
              type="text" 
              name="website" 
              style={{ display: "none" }} 
              tabIndex={-1}
              autoComplete="off"
            />

            <Button 
              type="submit" 
              className="btn-primary w-full"
              disabled={loggingIn}
              data-testid="admin-login-btn"
            >
              {loggingIn ? "Authenticating..." : "Login"}
            </Button>
          </form>

          <p className="text-text-muted text-xs text-center mt-8 font-mono">
            This area is protected. Unauthorized access attempts are logged.
          </p>
          </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,rgba(0,255,136,0.06),transparent_30%)] py-12" data-testid="admin-dashboard">
      <SEO
        title="Admin Panel"
        description="Restricted administration area for ZeroDay.log."
        canonical="/admin"
        robots="noindex,nofollow"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-2 font-mono text-sm text-text-muted">
              <span className="text-accent-primary">$</span>
              <span>sudo -i</span>
            </div>
            <h1 className="font-heading font-bold text-3xl text-text-primary flex items-center gap-2">
              <Shield className="w-8 h-8 text-accent-primary" />
              Admin Panel
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-7 text-text-secondary">
              Operational control surface for drafting, publishing, and reviewing writeups before they go live.
            </p>
          </div>
          <div className="grid gap-4 xl:min-w-[360px]">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="border border-border bg-background-surface p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-text-muted">Published</div>
                <div className="mt-2 font-heading text-2xl text-text-primary">{publishedCount}</div>
              </div>
              <div className="border border-border bg-background-surface p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-text-muted">Drafts</div>
                <div className="mt-2 font-heading text-2xl text-text-primary">{draftCount}</div>
              </div>
              <div className="border border-border bg-background-surface p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-text-muted">Views</div>
                <div className="mt-2 font-heading text-2xl text-text-primary">{totalViews}</div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/admin/writeup/new">
                <Button className="btn-primary" data-testid="admin-new-writeup-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Writeup
                </Button>
              </Link>
              <div className="inline-flex items-center gap-2 border border-border bg-background-surface px-4 py-2 font-mono text-xs text-text-muted">
                <Settings className="h-4 w-4 text-accent-primary" />
                editorial mode active
              </div>
              <Button 
                variant="ghost" 
                onClick={handleLogout}
                className="text-text-muted hover:text-accent-danger"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-background-surface border border-border p-6">
            <p className="text-text-muted text-xs font-mono uppercase tracking-wider mb-2">
              Total Writeups
            </p>
            <p className="text-accent-primary font-heading font-bold text-3xl">
              {writeups.length}
            </p>
          </div>
          <div className="bg-background-surface border border-border p-6">
            <p className="text-text-muted text-xs font-mono uppercase tracking-wider mb-2">
              Published
            </p>
            <p className="text-text-primary font-heading font-bold text-3xl">
              {publishedCount}
            </p>
          </div>
          <div className="bg-background-surface border border-border p-6">
            <p className="text-text-muted text-xs font-mono uppercase tracking-wider mb-2">
              Drafts
            </p>
            <p className="text-text-primary font-heading font-bold text-3xl">
              {draftCount}
            </p>
          </div>
        </div>

        {/* Writeups Table */}
        <div className="bg-background-surface border border-border">
          <div className="p-4 border-b border-border">
            <h2 className="font-heading font-bold text-lg text-text-primary flex items-center gap-2">
              <FileText className="w-5 h-5 text-accent-primary" />
              Manage Writeups
            </h2>
          </div>
          
          {writeups.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-text-muted mx-auto mb-4" />
              <p className="text-text-muted font-mono">No writeups yet</p>
              <Link to="/admin/writeup/new">
                <Button className="btn-primary mt-4">
                  Create Your First Writeup
                </Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {writeups.map((writeup) => (
                <div 
                  key={writeup.id} 
                  className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-medium text-text-primary truncate">
                        {writeup.title}
                      </h3>
                      {!writeup.published && (
                        <span className="text-xs font-mono bg-accent-secondary/20 text-accent-secondary px-2 py-0.5">
                          DRAFT
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-text-muted text-xs font-mono">
                      <span className={`capitalize ${
                        writeup.difficulty === "easy" ? "text-accent-primary" :
                        writeup.difficulty === "medium" ? "text-accent-secondary" :
                        writeup.difficulty === "hard" ? "text-orange-500" :
                        "text-accent-danger"
                      }`}>
                        {writeup.difficulty}
                      </span>
                      <span>{PLATFORM_LABELS[writeup.platform] || writeup.platform}</span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {writeup.views}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Link to={`/writeup/${writeup.id}`} target="_blank">
                      <Button variant="ghost" size="sm" className="text-text-muted hover:text-accent-primary">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Link to={`/admin/writeup/${writeup.id}`}>
                      <Button variant="ghost" size="sm" className="text-text-muted hover:text-accent-primary">
                        <Edit className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-text-muted hover:text-accent-danger"
                      onClick={() => handleDelete(writeup.id, writeup.title)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
